import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, X, AlertTriangle, ChevronRight } from 'lucide-react';
import { getSsgPeriods, getSsgSessions, createSsgSession } from '../../api';
import { SSG_COMPETITION_NAME } from '../../ssgConstants';

const card = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px', marginBottom: 10 };
const btn = (active, color = '#3b82f6') => ({
  padding: '7px 14px', borderRadius: 8, border: `1px solid ${active ? color : 'var(--border)'}`,
  background: active ? color : 'transparent', color: active ? '#fff' : 'var(--text-secondary)',
  fontSize: 12.5, fontWeight: active ? 600 : 400, cursor: 'pointer', fontFamily: 'inherit',
});
const inputStyle = {
  padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)',
  background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13,
  fontFamily: 'inherit', width: '100%', boxSizing: 'border-box',
};
const labelStyle = { fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4, display: 'block' };

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

// A session whose date has passed while it's still 'draft' is the exact failure mode this stage
// is designed against — surfaced here (sorted first) and again as a nav badge (PageChrome.js).
function isNotYetEntered(session) {
  return session.status === 'draft' && session.session_date?.slice(0, 10) < todayStr();
}

function NewSessionForm({ defaultSeason, defaultMatchday, onCreate, onClose }) {
  const [form, setForm] = useState({
    season: defaultSeason || '', session_date: todayStr(), matchday: defaultMatchday || '',
    title: '', format: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.season || !form.session_date || form.matchday === '') return;
    setSaving(true);
    try {
      await onCreate({ ...form, matchday: Number(form.matchday) });
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <div style={{ ...card, border: '1px solid #3b82f6' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>New session</div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}><X size={16} /></button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 }}>
        <div>
          <label style={labelStyle}>Date</label>
          <input style={inputStyle} type="date" value={form.session_date} onChange={e => set('session_date', e.target.value)} />
        </div>
        <div>
          <label style={labelStyle}>Matchday</label>
          <input style={inputStyle} type="number" min="1" max="38" value={form.matchday} onChange={e => set('matchday', e.target.value)} />
        </div>
        <div>
          <label style={labelStyle}>Title</label>
          <input style={inputStyle} placeholder="Optional" value={form.title} onChange={e => set('title', e.target.value)} />
        </div>
        <div>
          <label style={labelStyle}>Format</label>
          <input style={inputStyle} placeholder="e.g. 6v6" value={form.format} onChange={e => set('format', e.target.value)} />
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
        <button style={btn(false)} onClick={onClose}>Cancel</button>
        <button style={btn(true)} onClick={handleSave} disabled={saving || !form.season || !form.session_date || form.matchday === ''}>
          {saving ? 'Creating…' : 'Create session'}
        </button>
      </div>
    </div>
  );
}

function SessionRow({ session, onOpen }) {
  const notYetEntered = isNotYetEntered(session);
  return (
    <div
      onClick={() => onOpen(session.id)}
      style={{
        display: 'flex', alignItems: 'center', gap: 16, padding: '12px 16px', borderRadius: 10, cursor: 'pointer',
        background: notYetEntered ? 'rgba(239,68,68,0.08)' : 'var(--bg-app)',
        border: `1px solid ${notYetEntered ? 'rgba(239,68,68,0.4)' : 'var(--border-strong)'}`, marginBottom: 8,
      }}
    >
      <div style={{ width: 110, fontSize: 13, fontWeight: 600 }}>{fmtDate(session.session_date)}</div>
      <div style={{ width: 60, fontSize: 12, color: 'var(--text-muted)' }}>MD{session.matchday}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{session.title || 'Untitled session'}</div>
        {session.format && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{session.format}</div>}
      </div>
      {notYetEntered ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, color: '#ef4444' }}>
          <AlertTriangle size={12} /> Not yet entered
        </span>
      ) : (
        <span style={{
          fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 99,
          background: session.status === 'published' ? '#10b98122' : 'var(--border)',
          color: session.status === 'published' ? '#10b981' : 'var(--text-muted)',
        }}>
          {session.status === 'published' ? 'Published' : 'Draft'}
        </span>
      )}
      <div style={{ width: 90, textAlign: 'right', fontSize: 12, color: 'var(--text-secondary)' }}>{session.players_scored} scored</div>
      <div style={{ width: 90, textAlign: 'right', fontSize: 12, color: 'var(--text-secondary)' }}>{session.points_awarded} pts</div>
      <ChevronRight size={16} color="var(--text-dimmed)" />
    </div>
  );
}

export default function SsgSessionsList({ user, onOpenSession, onBackToStandings }) {
  const [periods, setPeriods] = useState([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewForm, setShowNewForm] = useState(false);

  useEffect(() => {
    getSsgPeriods().then(rows => {
      setPeriods(rows);
      const active = rows.find(p => p.status === 'active') || rows[0];
      setSelectedPeriodId(active?.id ?? null);
    });
  }, []);

  const refresh = useCallback(() => {
    if (!selectedPeriodId) return;
    setLoading(true);
    getSsgSessions({ period_id: selectedPeriodId, requesterRole: 'staff' })
      .then(rows => setSessions(rows || []))
      .finally(() => setLoading(false));
  }, [selectedPeriodId]);

  useEffect(() => { refresh(); }, [refresh]);

  const selectedPeriod = periods.find(p => p.id === selectedPeriodId);

  const sortedSessions = useMemo(() => {
    return [...sessions].sort((a, b) => {
      const aFlag = isNotYetEntered(a), bFlag = isNotYetEntered(b);
      if (aFlag !== bFlag) return aFlag ? -1 : 1;
      return new Date(b.session_date) - new Date(a.session_date);
    });
  }, [sessions]);

  const handleCreate = async (data) => {
    await createSsgSession(data, 'staff');
    refresh();
  };

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        {onBackToStandings && (
          <button onClick={onBackToStandings} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12.5, marginBottom: 8, padding: 0 }}>
            ← Back to standings
          </button>
        )}
        <h1 style={{ fontSize: 22, fontWeight: 700 }}>{SSG_COMPETITION_NAME} — Sessions</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 4 }}>Small-sided-games scoring sessions, one row per training that counted.</p>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {periods.map(p => (
          <button key={p.id} style={btn(p.id === selectedPeriodId)} onClick={() => setSelectedPeriodId(p.id)}>
            {p.name} {p.status === 'active' && '•'}
          </button>
        ))}
        <button style={{ ...btn(true), marginLeft: 'auto' }} onClick={() => setShowNewForm(s => !s)}>
          <Plus size={14} style={{ verticalAlign: -2, marginRight: 4 }} />New session
        </button>
      </div>

      {showNewForm && (
        <NewSessionForm
          defaultSeason={selectedPeriod?.season}
          defaultMatchday={selectedPeriod?.start_matchday}
          onCreate={handleCreate}
          onClose={() => setShowNewForm(false)}
        />
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-dimmed)' }}>Loading…</div>
      ) : sortedSessions.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-dimmed)' }}>No sessions in this period yet.</div>
      ) : (
        <div>
          <div style={{ display: 'flex', gap: 16, padding: '0 16px', marginBottom: 6, fontSize: 10, fontWeight: 700, color: 'var(--text-dimmed)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            <div style={{ width: 110 }}>Date</div>
            <div style={{ width: 60 }}>MD</div>
            <div style={{ flex: 1 }}>Session</div>
            <div style={{ width: 110 }}>Status</div>
            <div style={{ width: 90, textAlign: 'right' }}>Scored</div>
            <div style={{ width: 90, textAlign: 'right' }}>Points</div>
            <div style={{ width: 16 }} />
          </div>
          {sortedSessions.map(s => <SessionRow key={s.id} session={s} onOpen={onOpenSession} />)}
        </div>
      )}
    </div>
  );
}
